import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from '@/shared/shadcn/ui/dialog';

export interface SuccessDialogProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  description?: string;
  iconColor?: string;
  autoCloseDelay?: number;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
  titleClassName?: string;
  descriptionClassName?: string;
}

const SuccessDialog: React.FC<SuccessDialogProps> = ({
  isOpen,
  onClose,
  title = 'Successfully completed',
  description = 'The operation has been completed successfully.',
  iconColor = '#198754',
  autoCloseDelay = 3000,
  size = 'md',
  className = '',
  titleClassName = '',
  descriptionClassName = '',
}) => {
  // Auto-close functionality
  React.useEffect(() => {
    if (isOpen && autoCloseDelay && autoCloseDelay > 0) {
      const timer = setTimeout(() => {
        onClose();
      }, autoCloseDelay);
      
      return () => clearTimeout(timer);
    }
  }, [isOpen, autoCloseDelay, onClose]);

  const getSizeClasses = () => {
    switch (size) {
      case 'sm':
        return 'max-w-sm';
      case 'md':
        return 'max-w-md';
      case 'lg':
        return 'max-w-lg';
      case 'xl':
        return 'max-w-xl';
      default:
        return 'max-w-md';
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent 
        className={`bg-white box-border content-stretch flex flex-col gap-[0px] items-center justify-center p-[10px] rounded-lg-2plus [&>button]:hidden ${getSizeClasses()} ${className}`}
      >
        {/* Success Icon - Bootstrap Check Circle */}
        <div className="flex items-center justify-center">
          <i className="bi bi-check2-circle text-[90px] text-success"></i>
        </div>
        
        {/* Title Section */}
        <DialogTitle asChild>
          <div
            className={`w-min font-medium !leading-[0] min-w-full relative shrink-0 text-success text-2xl text-center [font-variation-settings:'wdth'_100] ${titleClassName}`}
          >
            <p className="leading-[1.2]">{title}</p>
          </div>
        </DialogTitle>

        {/* Description Section */}
        {description && (
          <DialogDescription asChild>
            <div
              className={`w-min font-normal leading-[0] min-w-full relative shrink-0 text-base py-3 text-black text-center [font-variation-settings:'wdth'_100] ${descriptionClassName}`}
            >
              <p className="leading-[1.5]">{description}</p>
            </div>
          </DialogDescription>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default SuccessDialog;
